import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  BriefcaseBusiness,
  CalendarRange,
  ChevronDown,
  Filter,
  Layers3,
  PiggyBank,
  ReceiptText,
  RotateCcw,
  TrendingUp,
  UserIcon,
  WalletCards,
} from 'lucide-react';

import DatePicker from '../components/DatePicker';
import { API_URL } from '../config';
import { safeFetch } from '../utils/safeFetch';
import { haptic } from '../utils/vibration';
import useToast from '../hooks/useToast';
import { formatTransactionFilterBoundary } from '../utils/transactionTime';
import { useInteractionPreferences } from '../contexts/InteractionPreferencesContext';

/* ==========================================================================
   Formatting & Calculation Helpers
   ========================================================================== */

const formatDateValue = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;

const formatCompactCurrency = (value) => {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  if (abs >= 1_000_000) return `${amount < 0 ? '-' : ''}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)} млн ₽`;
  if (abs >= 10_000) return `${amount < 0 ? '-' : ''}${(abs / 1_000).toFixed(abs >= 100_000 ? 0 : 1)} тыс ₽`;
  return formatCurrency(amount);
};

const formatPeriodLabel = (from, to) => {
  if (!from && !to) return 'Весь период';
  if (from && to) {
    const fromDate = new Date(`${from}T00:00:00`);
    const toDate = new Date(`${to}T00:00:00`);
    return `${fromDate.toLocaleDateString('ru-RU')} - ${toDate.toLocaleDateString('ru-RU')}`;
  }
  if (from) return `С ${new Date(`${from}T00:00:00`).toLocaleDateString('ru-RU')}`;
  return `По ${new Date(`${to}T00:00:00`).toLocaleDateString('ru-RU')}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();
  return {
    from: formatDateValue(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: formatDateValue(today),
  };
};

const getRollingRange = (days) => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(today.getDate() - (days - 1));
  return {
    from: formatDateValue(from),
    to: formatDateValue(today),
  };
};

const formatEmployeeShortName = (label) => {
  const parts = String(label || '').split(' (@')[0].trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return String(label || 'Не указано');
  const [lastName, firstName, middleName] = parts;
  const firstInitial = firstName?.[0] ? `${firstName[0].toUpperCase()}.` : '';
  const middleInitial = middleName?.[0] ? `${middleName[0].toUpperCase()}.` : '';
  return `${lastName} ${firstInitial}${middleInitial}`.trim();
};

/**
 * Creates smooth bezier curves like TradingView / Robinhood
 */
function buildSmoothSpline(pts) {
  if (!pts || pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;

  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(i - 1, 0)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(i + 2, pts.length - 1)];

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/* ==========================================================================
   Financial Dynamic Chart (Mobile Optimized + In-place Layout Stability)
   ========================================================================== */

const BrokerTrendChart = ({ points, onSelectPreset, currentPresetKey }) => {
  const { animationsEnabled } = useInteractionPreferences();
  const containerRef = useRef(null);
  const scrubTimerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(700);

  // selectedMetric: 'expense' | 'income' | 'both'
  const [selectedMetric, setSelectedMetric] = useState('expense');
  // chartType: 'line' | 'bar'
  const [chartType, setChartType] = useState('line');

  const [activeIdx, setActiveIdx] = useState(-1);
  const uid = useId().replace(/:/g, '');

  const isMobile = containerWidth < 500;
  const chartHeight = isMobile ? 210 : 250;
  const padding = useMemo(
    () =>
      isMobile
        ? { top: 16, right: 38, bottom: 22, left: 6 }
        : { top: 20, right: 52, bottom: 24, left: 14 },
    [isMobile]
  );

  // Measure container width
  useEffect(() => {
    if (!containerRef.current) return;
    const updateWidth = () => {
      if (containerRef.current) {
        const w = Math.round(containerRef.current.clientWidth || 700);
        if (w > 0) {
          setContainerWidth((prev) => (Math.abs(prev - w) > 2 ? w : prev));
        }
      }
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Aggregated totals
  const totalExpense = useMemo(
    () => points.reduce((acc, p) => acc + Number(p.expense || 0), 0),
    [points]
  );
  const totalIncome = useMemo(
    () => points.reduce((acc, p) => acc + Number(p.income || 0), 0),
    [points]
  );
  const totalOps = useMemo(
    () => points.reduce((acc, p) => acc + Number(p.count || 0), 0),
    [points]
  );

  const avgExpense = useMemo(
    () => (points.length ? Math.round(totalExpense / points.length) : 0),
    [points, totalExpense]
  );
  const avgIncome = useMemo(
    () => (points.length ? Math.round(totalIncome / points.length) : 0),
    [points, totalIncome]
  );

  const activeAvgDaily = selectedMetric === 'income' ? avgIncome : avgExpense;

  // Maximum value for scale
  const maxSeriesValue = useMemo(() => {
    let max = 1000;
    points.forEach((p) => {
      const exp = Number(p.expense || 0);
      const inc = Number(p.income || 0);
      if (selectedMetric === 'expense' && exp > max) max = exp;
      if (selectedMetric === 'income' && inc > max) max = inc;
      if (selectedMetric === 'both') {
        if (exp > max) max = exp;
        if (inc > max) max = inc;
      }
    });
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    const rounded = Math.ceil((max * 1.15) / magnitude) * magnitude;
    return Math.max(rounded, 10000);
  }, [points, selectedMetric]);

  const innerWidth = Math.max(10, containerWidth - padding.left - padding.right);
  const innerHeight = Math.max(10, chartHeight - padding.top - padding.bottom);

  const toX = useCallback(
    (index) => {
      if (points.length <= 1) return padding.left + innerWidth / 2;
      return padding.left + (innerWidth / (points.length - 1)) * index;
    },
    [innerWidth, padding.left, points.length]
  );

  const toY = useCallback(
    (value) => {
      const val = Number(value || 0);
      return padding.top + innerHeight - (val / maxSeriesValue) * innerHeight;
    },
    [innerHeight, maxSeriesValue, padding.top]
  );

  // Point coordinates
  const expenseCoords = useMemo(
    () => points.map((p, i) => ({ x: toX(i), y: toY(p.expense), raw: p })),
    [points, toX, toY]
  );

  const incomeCoords = useMemo(
    () => points.map((p, i) => ({ x: toX(i), y: toY(p.income), raw: p })),
    [points, toX, toY]
  );

  // Splines
  const expenseLineD = useMemo(() => buildSmoothSpline(expenseCoords), [expenseCoords]);
  const incomeLineD = useMemo(() => buildSmoothSpline(incomeCoords), [incomeCoords]);

  const bottomY = padding.top + innerHeight;
  const expenseAreaD = useMemo(() => {
    if (expenseCoords.length < 2) return '';
    const first = expenseCoords[0];
    const last = expenseCoords[expenseCoords.length - 1];
    return `${expenseLineD} L ${last.x.toFixed(1)} ${bottomY} L ${first.x.toFixed(1)} ${bottomY} Z`;
  }, [bottomY, expenseCoords, expenseLineD]);

  const incomeAreaD = useMemo(() => {
    if (incomeCoords.length < 2) return '';
    const first = incomeCoords[0];
    const last = incomeCoords[incomeCoords.length - 1];
    return `${incomeLineD} L ${last.x.toFixed(1)} ${bottomY} L ${first.x.toFixed(1)} ${bottomY} Z`;
  }, [bottomY, incomeCoords, incomeLineD]);

  // Y-axis ticks (3 on mobile, 4 on desktop)
  const yAxisTicks = useMemo(() => {
    const ticks = [];
    const count = isMobile ? 3 : 4;
    for (let i = 0; i <= count; i++) {
      const val = (maxSeriesValue / count) * i;
      let label = '0';
      if (val >= 1_000_000) {
        label = `${(val / 1_000_000).toFixed(1)}М`;
      } else if (val >= 1000) {
        label = `${Math.round(val / 1000)}k`;
      }
      ticks.push({ value: val, y: toY(val), label });
    }
    return ticks;
  }, [isMobile, maxSeriesValue, toY]);

  // X-axis dates
  const xLabels = useMemo(() => {
    if (points.length === 0) return [];
    if (isMobile) {
      const midIdx = Math.floor(points.length / 2);
      return [
        { ...points[0], idx: 0, x: toX(0) },
        ...(points.length > 5 ? [{ ...points[midIdx], idx: midIdx, x: toX(midIdx) }] : []),
        { ...points[points.length - 1], idx: points.length - 1, x: toX(points.length - 1) },
      ];
    }
    const step = points.length > 10 ? Math.ceil(points.length / 6) : 1;
    return points
      .map((p, idx) => ({ ...p, idx, x: toX(idx) }))
      .filter((p, idx) => idx === 0 || idx === points.length - 1 || idx % step === 0);
  }, [isMobile, points, toX]);

  // Scrubber interactive touch/mouse
  const handlePointer = useCallback(
    (clientX) => {
      if (!containerRef.current || points.length === 0) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, (x - padding.left) / innerWidth));
      const nextIdx = Math.round(ratio * (points.length - 1));
      if (nextIdx !== activeIdx && nextIdx >= 0 && nextIdx < points.length) {
        haptic('selection');
        setActiveIdx(nextIdx);
      }
    },
    [activeIdx, innerWidth, padding.left, points.length]
  );

  const onPointerStart = (clientX) => {
    if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current);
    handlePointer(clientX);
  };

  const onPointerMove = (clientX) => {
    if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current);
    handlePointer(clientX);
  };

  const onPointerEnd = () => {
    if (scrubTimerRef.current) clearTimeout(scrubTimerRef.current);
    scrubTimerRef.current = setTimeout(() => {
      setActiveIdx(-1);
    }, 1800);
  };

  const activePoint = activeIdx >= 0 && activeIdx < points.length ? points[activeIdx] : null;

  const displayValue = useMemo(() => {
    if (selectedMetric === 'income') {
      return activePoint ? Number(activePoint.income || 0) : totalIncome;
    }
    if (selectedMetric === 'expense') {
      return activePoint ? Number(activePoint.expense || 0) : totalExpense;
    }
    return activePoint ? Number(activePoint.expense || 0) : totalExpense;
  }, [activePoint, selectedMetric, totalExpense, totalIncome]);

  const deviation = useMemo(() => {
    if (!activePoint || activeAvgDaily === 0) return null;
    const currentVal =
      selectedMetric === 'income' ? Number(activePoint.income || 0) : Number(activePoint.expense || 0);
    const diff = currentVal - activeAvgDaily;
    const pct = Math.round((diff / activeAvgDaily) * 100);
    return { diff, pct };
  }, [activeAvgDaily, activePoint, selectedMetric]);

  const benchmarkY = toY(activeAvgDaily);

  return (
    <div className="rounded-[28px] border border-gray-200 bg-white p-3.5 shadow-sm sm:p-5 dark:border-gray-800 dark:bg-gray-900">
      {/* 1. TOP ROW: Title & Timeframe Presets (Rock-solid, never wraps) */}
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2.5 dark:border-gray-800/80">
        <div className="truncate text-xs font-semibold text-gray-500 dark:text-gray-400">
          {selectedMetric === 'income'
            ? 'Динамика доходов'
            : selectedMetric === 'both'
            ? 'Доходы и расходы'
            : 'Динамика расходов'}
        </div>

        {/* Stable Timeframe Buttons */}
        <div className="flex shrink-0 items-center rounded-xl border border-gray-200 bg-gray-50 p-0.5 text-xs font-medium dark:border-gray-800 dark:bg-gray-950/60">
          {[
            { key: '7', label: '7Д' },
            { key: '30', label: '30Д' },
            { key: 'month', label: 'Месяц' },
          ].map((preset) => {
            const isActive = currentPresetKey === preset.key;
            return (
              <button
                key={preset.key}
                type="button"
                onClick={() => {
                  haptic('selection');
                  onSelectPreset(preset.key);
                }}
                className={`h-7 px-2.5 rounded-lg text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. INSPECTION STATS: Fixed Height container so NOTHING jumps */}
      <div className="pt-2 pb-1.5">
        <div className="flex h-4 items-center justify-between text-[11px] font-medium text-gray-400 dark:text-gray-500">
          <span className="truncate">
            {activePoint
              ? `${activePoint.label}${selectedMetric === 'both' ? ' • Расход / Доход' : ''}`
              : selectedMetric === 'both'
              ? 'Расход / Доход за период'
              : 'Итого за выбранный период'}
          </span>
          <span className="shrink-0 tabular-nums">
            {activePoint ? `${Number(activePoint.count || 0)} операций` : `${points.length} дн. • ${totalOps} оп.`}
          </span>
        </div>

        <div className="mt-0.5 flex h-9 items-center justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-2">
            <span
              className={`truncate text-2xl font-extrabold tracking-tight tabular-nums sm:text-3xl ${
                selectedMetric === 'income'
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-rose-600 dark:text-rose-400'
              }`}
            >
              {formatCurrency(displayValue)}
            </span>

            {selectedMetric === 'both' ? (
              <span className="shrink-0 text-xs font-semibold tabular-nums text-emerald-600 sm:text-sm dark:text-emerald-400">
                / {formatCurrency(activePoint ? activePoint.income : totalIncome)}
              </span>
            ) : null}
          </div>

          <div className="shrink-0">
            {activePoint && deviation !== null ? (
              <span
                className={`inline-flex items-center rounded-lg px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
                  deviation.pct > 0
                    ? selectedMetric === 'income'
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                    : selectedMetric === 'income'
                    ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                    : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                }`}
              >
                {deviation.pct > 0 ? `+${deviation.pct}%` : `${deviation.pct}%`} к ср.
              </span>
            ) : (
              <span className="inline-flex items-center rounded-lg bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                Ср: {formatCompactCurrency(activeAvgDaily)}/дн
              </span>
            )}
          </div>
        </div>
      </div>

      {/* 3. CONTROLS ROW: Metric Segmented Bar + View Type Toggle (Locked height, never wraps) */}
      <div className="flex items-center gap-2 border-t border-gray-100 pt-2 dark:border-gray-800/80">
        {/* Metric Segmented Bar: 3 equal buttons */}
        <div className="grid flex-1 grid-cols-3 rounded-xl border border-gray-200 bg-gray-50 p-0.5 text-xs dark:border-gray-800 dark:bg-gray-950/50">
          {[
            { id: 'expense', label: 'Расход', activeClass: 'bg-white text-rose-600 shadow-xs dark:bg-gray-800 dark:text-rose-400' },
            { id: 'income', label: 'Доход', activeClass: 'bg-white text-emerald-600 shadow-xs dark:bg-gray-800 dark:text-emerald-400' },
            { id: 'both', label: 'Оба', activeClass: 'bg-white text-blue-600 shadow-xs dark:bg-gray-800 dark:text-blue-400' },
          ].map((tab) => {
            const isActive = selectedMetric === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  haptic('selection');
                  setSelectedMetric(tab.id);
                }}
                className={`h-8 rounded-lg text-center font-medium transition-colors ${
                  isActive
                    ? tab.activeClass
                    : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* View Type Toggle: 2 stable icon buttons */}
        <div className="flex shrink-0 items-center rounded-xl border border-gray-200 bg-gray-50 p-0.5 text-xs dark:border-gray-800 dark:bg-gray-950/50">
          <button
            type="button"
            onClick={() => {
              haptic('selection');
              setChartType('line');
            }}
            title="Линия"
            aria-label="Линейный график"
            className={`flex h-8 w-9 items-center justify-center rounded-lg transition-colors ${
              chartType === 'line'
                ? 'bg-white text-gray-950 shadow-xs dark:bg-gray-800 dark:text-white'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <TrendingUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('selection');
              setChartType('bar');
            }}
            title="Столбики"
            aria-label="Столбчатый график"
            className={`flex h-8 w-9 items-center justify-center rounded-lg transition-colors ${
              chartType === 'bar'
                ? 'bg-white text-gray-950 shadow-xs dark:bg-gray-800 dark:text-white'
                : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <BarChart3 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* 4. SVG Responsive Canvas */}
      <div
        ref={containerRef}
        onMouseMove={(e) => onPointerMove(e.clientX)}
        onMouseLeave={onPointerEnd}
        onTouchStart={(e) => {
          if (e.touches?.[0]) onPointerStart(e.touches[0].clientX);
        }}
        onTouchMove={(e) => {
          if (e.touches?.[0]) onPointerMove(e.touches[0].clientX);
        }}
        onTouchEnd={onPointerEnd}
        className="relative mt-2.5 w-full cursor-crosshair select-none touch-none"
        style={{ height: `${chartHeight}px` }}
      >
        {points.length === 0 ? (
          <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-gray-200 text-xs text-gray-400 dark:border-gray-800">
            Нет данных за выбранный период
          </div>
        ) : (
          <svg
            width="100%"
            height={chartHeight}
            className="overflow-visible"
            style={{ width: '100%', height: `${chartHeight}px` }}
          >
            <defs>
              <linearGradient id={`expenseGrad-${uid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.30" />
                <stop offset="75%" stopColor="#f43f5e" stopOpacity="0.03" />
                <stop offset="100%" stopColor="#f43f5e" stopOpacity="0.0" />
              </linearGradient>
              <linearGradient id={`incomeGrad-${uid}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.28" />
                <stop offset="75%" stopColor="#10b981" stopOpacity="0.03" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
              </linearGradient>
              <filter id={`glow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Horizontal Grid Lines & Y-Axis Labels */}
            {yAxisTicks.map((tick, i) => (
              <g key={i}>
                <line
                  x1={padding.left}
                  x2={containerWidth - padding.right}
                  y1={tick.y}
                  y2={tick.y}
                  stroke="currentColor"
                  className="text-gray-100 dark:text-gray-800/80"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
                <text
                  x={containerWidth - (isMobile ? 2 : 8)}
                  y={tick.y + 3}
                  textAnchor="end"
                  className="fill-gray-400 text-[10px] font-medium tabular-nums dark:fill-gray-500"
                >
                  {tick.label}
                </text>
              </g>
            ))}

            {/* Benchmark Line (Daily Average) */}
            {activeAvgDaily > 0 && selectedMetric !== 'both' ? (
              <g>
                <line
                  x1={padding.left}
                  x2={containerWidth - padding.right}
                  y1={benchmarkY}
                  y2={benchmarkY}
                  stroke="#f59e0b"
                  strokeWidth="1.25"
                  strokeDasharray="4 4"
                  opacity="0.8"
                />
                <text
                  x={padding.left + 4}
                  y={benchmarkY - 5}
                  fill="#f59e0b"
                  className="text-[9px] font-bold tabular-nums"
                  opacity="0.9"
                >
                  Ср. в день: {formatCompactCurrency(activeAvgDaily)}
                </text>
              </g>
            ) : null}

            {/* BAR MODE */}
            {chartType === 'bar' ? (
              <g>
                {points.map((p, i) => {
                  const centerX = toX(i);
                  const isHovered = activeIdx === i;

                  if (selectedMetric === 'both') {
                    const barW = Math.max(3, Math.min(isMobile ? 7 : 14, (innerWidth / points.length) * 0.4));
                    const incY = toY(p.income);
                    const expY = toY(p.expense);
                    const incH = Math.max(2, bottomY - incY);
                    const expH = Math.max(2, bottomY - expY);

                    return (
                      <g key={i}>
                        <rect
                          x={centerX - barW - 1}
                          y={incY}
                          width={barW}
                          height={incH}
                          rx={2}
                          fill="#10b981"
                          opacity={isHovered ? 1 : 0.78}
                        />
                        <rect
                          x={centerX + 1}
                          y={expY}
                          width={barW}
                          height={expH}
                          rx={2}
                          fill="#f43f5e"
                          opacity={isHovered ? 1 : 0.78}
                        />
                      </g>
                    );
                  }

                  const barW = Math.max(3, Math.min(isMobile ? 12 : 22, (innerWidth / points.length) * 0.72));
                  const val = selectedMetric === 'income' ? p.income : p.expense;
                  const y = toY(val);
                  const h = Math.max(2, bottomY - y);
                  const color = selectedMetric === 'income' ? '#10b981' : '#f43f5e';

                  return (
                    <rect
                      key={i}
                      x={centerX - barW / 2}
                      y={y}
                      width={barW}
                      height={h}
                      rx={2.5}
                      fill={color}
                      opacity={isHovered ? 1 : 0.82}
                    />
                  );
                })}
              </g>
            ) : null}

            {/* LINE MODE */}
            {chartType === 'line' ? (
              <>
                {(selectedMetric === 'income' || selectedMetric === 'both') && incomeCoords.length > 1 ? (
                  <g>
                    <path d={incomeAreaD} fill={`url(#incomeGrad-${uid})`} />
                    <path
                      d={incomeLineD}
                      fill="none"
                      stroke="#10b981"
                      strokeWidth={isMobile ? 2.2 : 2.6}
                      strokeLinecap="round"
                    />
                  </g>
                ) : null}

                {(selectedMetric === 'expense' || selectedMetric === 'both') && expenseCoords.length > 1 ? (
                  <g>
                    <path d={expenseAreaD} fill={`url(#expenseGrad-${uid})`} />
                    <path
                      d={expenseLineD}
                      fill="none"
                      stroke="#f43f5e"
                      strokeWidth={isMobile ? 2.2 : 2.6}
                      strokeLinecap="round"
                      filter={animationsEnabled ? `url(#glow-${uid})` : undefined}
                    />
                  </g>
                ) : null}
              </>
            ) : null}

            {/* Scrubber Crosshair & Indicators */}
            {activeIdx >= 0 && activeIdx < points.length ? (
              <g className="pointer-events-none">
                <line
                  x1={toX(activeIdx)}
                  x2={toX(activeIdx)}
                  y1={padding.top}
                  y2={bottomY}
                  stroke="currentColor"
                  className="text-gray-400 dark:text-gray-500"
                  strokeWidth="1.25"
                  strokeDasharray="3 3"
                />

                {selectedMetric !== 'income' && chartType === 'line' ? (
                  <>
                    <circle
                      cx={toX(activeIdx)}
                      cy={toY(points[activeIdx].expense)}
                      r="8"
                      fill="#f43f5e"
                      opacity="0.25"
                    />
                    <circle
                      cx={toX(activeIdx)}
                      cy={toY(points[activeIdx].expense)}
                      r="4"
                      fill="#ffffff"
                      stroke="#f43f5e"
                      strokeWidth="2.5"
                    />
                  </>
                ) : null}

                {(selectedMetric === 'income' || selectedMetric === 'both') && chartType === 'line' ? (
                  <>
                    <circle
                      cx={toX(activeIdx)}
                      cy={toY(points[activeIdx].income)}
                      r="8"
                      fill="#10b981"
                      opacity="0.25"
                    />
                    <circle
                      cx={toX(activeIdx)}
                      cy={toY(points[activeIdx].income)}
                      r="4"
                      fill="#ffffff"
                      stroke="#10b981"
                      strokeWidth="2.5"
                    />
                  </>
                ) : null}
              </g>
            ) : null}
          </svg>
        )}
      </div>

      {/* X-Axis Dates */}
      <div className="mt-1 flex items-center justify-between border-t border-gray-100 px-1 pt-1.5 text-[10px] sm:text-[11px] text-gray-400 dark:border-gray-800 dark:text-gray-500">
        {xLabels.map((lbl) => (
          <span key={lbl.idx} className="font-medium">
            {lbl.label}
          </span>
        ))}
      </div>

      {/* Subtle Hint */}
      <div className="mt-2 text-center text-[11px] text-gray-400 dark:text-gray-500">
        Проведите по графику для просмотра любого дня
      </div>
    </div>
  );
};

/* ==========================================================================
   Skeleton Loaders
   ========================================================================== */

const AnalyticsSkeleton = () => (
  <div className="space-y-4">
    <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="h-5 w-40 animate-pulse rounded bg-gray-200 dark:bg-gray-800" />
      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        {[0, 1, 2].map((item) => (
          <div key={item} className="h-20 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800/80" />
        ))}
      </div>
    </div>
    <div className="h-[320px] animate-pulse rounded-[28px] bg-gray-100 dark:bg-gray-900" />
  </div>
);

/* ==========================================================================
   Main ExpensesAnalytics Page Component
   ========================================================================== */

const ExpensesAnalytics = () => {
  const token = localStorage.getItem('token');
  const currentUserType = localStorage.getItem('user_type');
  const isAdminLike = currentUserType === 'admin' || currentUserType === 'developer';

  const currentMonthRange = useMemo(() => getCurrentMonthRange(), []);
  const [appliedRange, setAppliedRange] = useState(currentMonthRange);
  const [draftRange, setDraftRange] = useState(currentMonthRange);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const [analytics, setAnalytics] = useState(null);
  const [salaryOpen, setSalaryOpen] = useState(true);
  const [employeeFundsOpen, setEmployeeFundsOpen] = useState(true);
  const [currentPresetKey, setCurrentPresetKey] = useState('month');

  const periodLabel = useMemo(
    () => formatPeriodLabel(appliedRange.from, appliedRange.to),
    [appliedRange.from, appliedRange.to]
  );

  const quickRanges = useMemo(
    () => [
      { key: '7', label: '7 дней', getRange: () => getRollingRange(7) },
      { key: '30', label: '30 дней', getRange: () => getRollingRange(30) },
      { key: 'month', label: 'Месяц', getRange: () => getCurrentMonthRange() },
    ],
    []
  );

  const appliedRangeRef = useRef(appliedRange);
  appliedRangeRef.current = appliedRange;

  const loadAnalytics = useCallback(
    async (range) => {
      if (!isAdminLike) return;
      const targetRange = range || appliedRangeRef.current;
      // If we already have data, don't replace screen with skeleton (avoids jarring layout jump)
      if (!hasLoadedRef.current) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError('');

      try {
        const search = new URLSearchParams();
        const dateFrom = formatTransactionFilterBoundary(targetRange.from, false);
        const dateTo = formatTransactionFilterBoundary(targetRange.to, true);
        if (dateFrom) search.set('date_from', dateFrom);
        if (dateTo) search.set('date_to', dateTo);

        const query = search.toString();
        const response = await safeFetch(`${API_URL}/api/transactions/analytics${query ? `?${query}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAnalytics(response);
        hasLoadedRef.current = true;
      } catch (e) {
        const msg = e.message || 'Не удалось загрузить аналитику';
        setError(msg);
        showToast(msg, 'error');
      } finally {
        setLoading(false);
        setIsRefreshing(false);
      }
    },
    [isAdminLike, token]
  );

  useEffect(() => {
    loadAnalytics(appliedRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedRange.from, appliedRange.to, loadAnalytics]);

  const handleApplyPreset = (key) => {
    const preset = quickRanges.find((p) => p.key === key);
    if (!preset) return;
    setCurrentPresetKey(key);
    const range = preset.getRange();
    setDraftRange(range);
    setAppliedRange(range);
  };

  const handleApplyFilters = () => {
    haptic('success');
    setCurrentPresetKey('custom');
    setAppliedRange(draftRange);
    setIsFilterOpen(false);
  };

  const handleResetFilters = () => {
    const next = getCurrentMonthRange();
    haptic('selection');
    setCurrentPresetKey('month');
    setDraftRange(next);
    setAppliedRange(next);
    setIsFilterOpen(false);
  };

  const categories = analytics?.categories || [];
  const salaryCategory = categories.find((item) => item.is_salary) || null;
  const otherCategories = categories.filter((item) => !item.is_salary);
  const summary = analytics?.summary || { income: 0, expense: 0, net: 0, count: 0 };
  const incomeBreakdown = analytics?.income_breakdown || [];
  const trend = analytics?.trend || [];
  const employeeFunds = analytics?.employee_funds || { total: 0, count: 0, employees: [] };

  if (!isAdminLike) {
    return (
      <div className="rounded-[28px] border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
        <div className="text-sm font-medium text-gray-950 dark:text-white">
          Аналитика расходов доступна только администраторам.
        </div>
      </div>
    );
  }

  return (
    <div className="desktop-content-container-wide space-y-4 animate-fade-in pb-10">
      {/* Top Filter & Period Control Bar */}
      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-[28px] border border-gray-200 bg-white p-3.5 sm:p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
              Выбранный период
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
              <CalendarRange className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
              <span className="truncate">{periodLabel}</span>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('tap');
                setDraftRange(appliedRange);
                setIsFilterOpen((prev) => !prev);
              }}
              className={`inline-flex h-9 sm:h-10 items-center gap-1.5 rounded-2xl border px-3 text-xs font-semibold transition-colors ${
                isFilterOpen
                  ? 'border-blue-300 bg-blue-50 text-blue-600 dark:border-blue-500 dark:bg-blue-950/30 dark:text-blue-300'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span>Календарь</span>
            </button>
          </div>
        </div>

        {/* Expandable Custom Range Selector */}
        <AnimatePresence initial={false}>
          {isFilterOpen ? (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-4 rounded-[24px] border border-gray-100 bg-gray-50/80 p-3 sm:p-4 dark:border-gray-800 dark:bg-gray-950/40">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <DatePicker
                      value={draftRange.from}
                      onChange={(value) => setDraftRange((prev) => ({ ...prev, from: value }))}
                      placeholder="Дата от"
                      allowPastDates
                      showTodayButton={false}
                      inputClassName="!rounded-2xl !border-gray-200 dark:!border-gray-700 !bg-white dark:!bg-gray-900 !py-3 !pl-8 !pr-8 !text-left !text-sm !font-medium"
                    />
                    <DatePicker
                      value={draftRange.to}
                      onChange={(value) => setDraftRange((prev) => ({ ...prev, to: value }))}
                      placeholder="Дата до"
                      allowPastDates
                      showTodayButton={false}
                      inputClassName="!rounded-2xl !border-gray-200 dark:!border-gray-700 !bg-white dark:!bg-gray-900 !py-3 !pl-8 !pr-8 !text-left !text-sm !font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={handleResetFilters}
                      className="inline-flex items-center justify-center gap-1.5 rounded-2xl border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                      <RotateCcw className="h-4 w-4" />
                      Сбросить
                    </button>
                    <button
                      type="button"
                      onClick={handleApplyFilters}
                      className="rounded-2xl bg-blue-600 px-3 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                    >
                      Применить
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.section>

      {error ? (
        <div className="rounded-[24px] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
          {error}
        </div>
      ) : null}

      {loading && !analytics ? (
        <AnalyticsSkeleton />
      ) : (
        <div className={`space-y-4 transition-opacity duration-200 ${isRefreshing ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
          {/* Top 3 KPI Cards: Mobile Optimized Grid */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {/* Income */}
            <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:rounded-[26px] sm:p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                <span>Доход</span>
                <ArrowUpRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              </div>
              <div className="mt-1 text-sm font-bold tracking-tight tabular-nums text-gray-950 sm:text-2xl sm:mt-1.5 dark:text-white truncate">
                {formatCompactCurrency(summary.income)}
              </div>
              <div className="hidden sm:block mt-1 text-xs text-gray-400 truncate">
                Реальные поступления
              </div>
            </div>

            {/* Expense */}
            <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:rounded-[26px] sm:p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between gap-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400">
                <span>Расход</span>
                <ArrowDownRight className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              </div>
              <div className="mt-1 text-sm font-bold tracking-tight tabular-nums text-gray-950 sm:text-2xl sm:mt-1.5 dark:text-white truncate">
                {formatCompactCurrency(summary.expense)}
              </div>
              <div className="hidden sm:block mt-1 text-xs text-gray-400 truncate">
                По компании и штату
              </div>
            </div>

            {/* Net / Balance */}
            <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:rounded-[26px] sm:p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between gap-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                <span>Итог</span>
                <PiggyBank className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
              </div>
              <div
                className={`mt-1 text-sm font-bold tracking-tight tabular-nums sm:text-2xl sm:mt-1.5 truncate ${
                  summary.net >= 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-rose-600 dark:text-rose-400'
                }`}
              >
                {formatCompactCurrency(summary.net)}
              </div>
              <div className="hidden sm:block mt-1 text-xs text-gray-400 truncate">
                {summary.income > 0 ? `Маржа: ${Math.round((summary.net / summary.income) * 100)}%` : 'Сальдо'}
              </div>
            </div>
          </div>

          {/* MAIN DYNAMIC FINANCIAL CHART */}
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <BrokerTrendChart
              points={trend}
              currentPresetKey={currentPresetKey}
              onSelectPreset={handleApplyPreset}
            />
          </motion.div>

          {/* 2-COLUMN LOWER SECTION: Categories & Finances */}
          <div className="grid gap-4 xl:grid-cols-[1.12fr,0.88fr]">
            {/* LEFT COLUMN: Categories breakdown */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <section className="rounded-[28px] border border-gray-200 bg-white p-4 shadow-sm sm:p-5 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-violet-500/10 text-violet-600 dark:bg-violet-950/30 dark:text-violet-400">
                      <ReceiptText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-gray-950 dark:text-white">
                        Расходы по категориям
                      </div>
                      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        По компании и сотрудникам
                      </div>
                    </div>
                  </div>

                  <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {categories.length} категорий
                  </span>
                </div>

                <div className="mt-4 space-y-3">
                  {/* Salary Accordion Card */}
                  {salaryCategory ? (
                    <div className="overflow-hidden rounded-[24px] border border-violet-200/80 bg-violet-50/60 transition-all dark:border-violet-900/60 dark:bg-violet-950/20">
                      <button
                        type="button"
                        onClick={() => {
                          haptic('selection');
                          setSalaryOpen((prev) => !prev);
                        }}
                        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-2.5">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white shadow-xs text-violet-600 dark:bg-violet-900/40 dark:text-violet-300">
                              <BriefcaseBusiness className="h-4.5 w-4.5" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-bold text-gray-950 dark:text-white">Зарплата</div>
                              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                {salaryCategory.count} выплат сотрудникам
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="text-sm font-bold text-violet-700 dark:text-violet-300 tabular-nums">
                              {formatCurrency(salaryCategory.total)}
                            </div>
                            <div className="mt-0.5 text-[11px] font-medium text-gray-500 dark:text-gray-400">
                              {salaryCategory.percent_of_expenses}% расходов
                            </div>
                          </div>
                          <ChevronDown
                            className={`h-4.5 w-4.5 shrink-0 text-violet-500 transition-transform ${
                              salaryOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </div>
                      </button>

                      <AnimatePresence initial={false}>
                        {salaryOpen ? (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden border-t border-violet-200/60 dark:border-violet-900/50"
                          >
                            <div className="space-y-2 bg-white/40 p-3 dark:bg-violet-950/10">
                              {(salaryCategory.employees || []).length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-violet-200 bg-white/70 px-4 py-4 text-sm text-gray-500 dark:border-violet-900 dark:bg-violet-950/10 dark:text-gray-400">
                                  За выбранный период нет выплаченных зарплат.
                                </div>
                              ) : (
                                salaryCategory.employees.map((employee, index) => (
                                  <div
                                    key={`${employee.user_id || 'salary'}-${index}`}
                                    className="flex items-center justify-between gap-3 rounded-2xl bg-white px-3.5 py-2.5 shadow-xs dark:bg-gray-900"
                                  >
                                    <div className="min-w-0">
                                      <div className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100">
                                        {formatEmployeeShortName(employee.label)}
                                      </div>
                                      <div className="mt-0.5 text-[11px] text-gray-400">
                                        {employee.count} выплат
                                      </div>
                                    </div>
                                    <div className="shrink-0 text-xs font-bold text-violet-700 dark:text-violet-300 tabular-nums">
                                      {formatCurrency(employee.total)}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </div>
                  ) : null}

                  {/* Other Categories */}
                  {otherCategories.length === 0 ? (
                    <div className="rounded-[24px] border border-dashed border-gray-200 bg-gray-50/80 px-4 py-5 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-400">
                      За выбранный период нет расходов по категориям.
                    </div>
                  ) : (
                    otherCategories.map((category, index) => {
                      const pct = Number(category.percent_of_expenses || 0);
                      return (
                        <div
                          key={`${category.key}-${index}`}
                          className="rounded-[24px] border border-gray-100 bg-gray-50/70 p-4 transition-all hover:border-gray-200 dark:border-gray-800 dark:bg-gray-950/40 dark:hover:border-gray-700"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-950 dark:text-white">
                                {category.label}
                              </div>
                              <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                {category.count} операций
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                                {formatCurrency(category.total)}
                              </div>
                              <div className="mt-0.5 text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                                {pct}% расходов
                              </div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-200/80 dark:bg-gray-800">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500"
                              style={{ width: `${Math.max(6, Math.min(100, pct))}%` }}
                            />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </section>
            </motion.div>

            {/* RIGHT COLUMN: Payment Methods & Employee Funds */}
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              {/* Payment Methods */}
              <section className="rounded-[28px] border border-gray-200 bg-white p-4 shadow-sm sm:p-5 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400">
                    <WalletCards className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-950 dark:text-white">Способы оплаты</div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Реальные оплаты заказов: нал и безнал
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-2.5">
                  {incomeBreakdown.length === 0 ? (
                    <div className="rounded-[24px] border border-dashed border-gray-200 bg-gray-50/80 px-4 py-5 text-sm text-gray-500 dark:border-gray-800 dark:bg-gray-950/40 dark:text-gray-400">
                      За выбранный период нет оплаченных заказов.
                    </div>
                  ) : (
                    incomeBreakdown.map((item, index) => (
                      <div
                        key={`${item.key}-${index}`}
                        className="flex items-center justify-between gap-3 rounded-[22px] border border-emerald-100 bg-emerald-50/60 p-3.5 dark:border-emerald-900/40 dark:bg-emerald-950/20"
                      >
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-gray-950 dark:text-white">
                            {item.label}
                          </div>
                          <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            {item.count} операций
                          </div>
                        </div>
                        <div className="text-sm font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
                          {formatCurrency(item.total)}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>

              {/* Funds in Hand */}
              <section className="rounded-[28px] border border-gray-200 bg-white p-4 shadow-sm sm:p-5 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400">
                    <UserIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-950 dark:text-white">Средства на руках</div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Текущие остатки на балансах сотрудников
                    </div>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 rounded-[22px] border border-amber-100 bg-amber-50/70 p-3.5 dark:border-amber-900/40 dark:bg-amber-950/20">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-gray-950 dark:text-white">
                        Общая сумма на руках
                      </div>
                      <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                        {employeeFunds.count}{' '}
                        {employeeFunds.count === 1
                          ? 'сотрудник'
                          : employeeFunds.count >= 2 && employeeFunds.count <= 4
                          ? 'сотрудника'
                          : 'сотрудников'}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-amber-700 dark:text-amber-300 tabular-nums">
                      {formatCurrency(employeeFunds.total)}
                    </div>
                  </div>

                  {/* Expandable Employee List */}
                  <div className="overflow-hidden rounded-[22px] border border-gray-100 bg-gray-50/80 dark:border-gray-800 dark:bg-gray-950/40">
                    <button
                      type="button"
                      onClick={() => {
                        haptic('selection');
                        setEmployeeFundsOpen((prev) => !prev);
                      }}
                      className="flex w-full items-center justify-between gap-3 p-3.5 text-left"
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-gray-950 dark:text-white">
                          Разбивка по сотрудникам
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                          Текущий баланс каждого
                        </div>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${
                          employeeFundsOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    <AnimatePresence initial={false}>
                      {employeeFundsOpen ? (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden border-t border-gray-100 dark:border-gray-800"
                        >
                          <div className="space-y-2 p-3">
                            {employeeFunds.employees.length === 0 ? (
                              <div className="rounded-xl border border-dashed border-gray-200 bg-white/80 p-3 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
                                Сейчас ни у кого нет остатка на руках.
                              </div>
                            ) : (
                              employeeFunds.employees.map((employee, index) => (
                                <div
                                  key={`${employee.user_id || 'employee-funds'}-${index}`}
                                  className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 shadow-xs dark:bg-gray-900"
                                >
                                  <div className="min-w-0">
                                    <div className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100">
                                      {formatEmployeeShortName(employee.label)}
                                    </div>
                                    <div className="text-[10px] text-gray-400">
                                      {employee.user_type === 'developer'
                                        ? 'Разработчик'
                                        : employee.user_type === 'admin'
                                        ? 'Администратор'
                                        : employee.user_type === 'dispatcher'
                                        ? 'Диспетчер'
                                        : 'Сотрудник'}
                                    </div>
                                  </div>
                                  <div
                                    className={`shrink-0 text-xs font-bold tabular-nums ${
                                      Number(employee.balance || 0) < 0
                                        ? 'text-rose-600 dark:text-rose-400'
                                        : 'text-amber-600 dark:text-amber-400'
                                    }`}
                                  >
                                    {formatCurrency(employee.balance)}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                </div>
              </section>

              {/* Quick Summary Metrics */}
              <section className="rounded-[28px] border border-gray-200 bg-white p-4 shadow-sm sm:p-5 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-500/10 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400">
                    <Layers3 className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-950 dark:text-white">Сводка периода</div>
                    <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Средние чеки и интенсивность
                    </div>
                  </div>
                </div>

                <div className="mt-3.5 space-y-2">
                  <div className="flex items-center justify-between rounded-2xl bg-gray-50 px-3.5 py-2.5 text-xs dark:bg-gray-950/40">
                    <span className="text-gray-600 dark:text-gray-400">Всего операций</span>
                    <span className="font-bold tabular-nums text-gray-950 dark:text-white">{summary.count}</span>
                  </div>

                  <div className="flex items-center justify-between rounded-2xl bg-gray-50 px-3.5 py-2.5 text-xs dark:bg-gray-950/40">
                    <span className="text-gray-600 dark:text-gray-400">Средний доход на операцию</span>
                    <span className="font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {incomeBreakdown.length
                        ? formatCurrency(
                            summary.income /
                              Math.max(
                                1,
                                incomeBreakdown.reduce((sum, item) => sum + item.count, 0)
                              )
                          )
                        : formatCurrency(0)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between rounded-2xl bg-gray-50 px-3.5 py-2.5 text-xs dark:bg-gray-950/40">
                    <span className="text-gray-600 dark:text-gray-400">Средний расход на операцию</span>
                    <span className="font-bold tabular-nums text-rose-600 dark:text-rose-400">
                      {categories.length
                        ? formatCurrency(
                            summary.expense /
                              Math.max(
                                1,
                                categories.reduce((sum, item) => sum + item.count, 0)
                              )
                          )
                        : formatCurrency(0)}
                    </span>
                  </div>
                </div>
              </section>
            </motion.div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesAnalytics;
